import Grid from '@material-ui/core/Grid';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import Logos from '@serko/zui/es/Logos/Logos';
import classNames from 'classnames';
import React, { Dispatch, FunctionComponent } from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Action } from 'redux';
import {
  ItineraryStatus,
  SaviSelectIntlAirSegmentDetail,
} from '../../models/itinerary';
import { AppState } from '../../store/model';
import { combinationInit } from '../../store/reducers/itinerary/actions';
import { getStatus } from '../../store/reducers/itinerary/selectors';
import { spacings } from '../lab/spacings';
import moment from 'moment';
import { AirLogo } from '../Segment/Content/Option/Columns/ProviderColumn/ProviderLogo/Logos/AirLogo';

interface OwnProps {
  isSelected?: boolean;
  airDetail?: SaviSelectIntlAirSegmentDetail;
  isLastFlight?: boolean;
}

interface StateProps {
  status?: ItineraryStatus;
}

interface DispatchProps {
  runInit: void;
}

interface CombinationRouteDetailProps
  extends OwnProps,
    StateProps,
    DispatchProps,
    WithStyles<typeof styles>,
    InjectedIntlProps {}

const _CombinationRouteDetailBase: FunctionComponent<
  CombinationRouteDetailProps
> = ({
  classes,
  isSelected,
  airDetail,
  isLastFlight,
  intl: { formatTime },
}) => {
  if (!airDetail) {
    return null;
  }
  return (
    <Grid
      container
      className={classNames(classes.routeDetailWarp, {
        [classes.borderRight]: isLastFlight,
        [classes.backGroundColorSelected]: isSelected,
      })}
    >
      <Grid container className={classes.routeDetail}>
        <Grid container>
          <Typography variant="body1">
            {moment(airDetail.departureDateTime).format('ddd D MMM')}
          </Typography>
        </Grid>
        <Grid className={classNames(classes.routeDetailTime,classes.paddingTopUnit)} >
          <Grid
              className={classNames(
                  classes.routeDetailTimeExtend,
                  classes.routeDetailTime,
                  classes.timeLineHeight,
              )}
          >
            <Grid sm={12}>
              <Typography className={classNames(classes.routeDetailTimeText,classes.timeLineHeight)} noWrap>
                {formatTime(airDetail.departureDateTime)} -{' '}
                {formatTime(airDetail.arrivalDateTime)}
                {airDetail && airDetail.overnight > 0 && (
                    <sup style={{fontWeight: 500, fontSize: 10}}>
                      +{airDetail.overnight}
                    </sup>
                )}
                <br />
                ({airDetail.duration})
              </Typography>
            </Grid>
          </Grid>
          <Grid className={classes.routeDetailTime}>
            <Typography className={classes.routeDetailCity}>
              {airDetail.route}
            </Typography>
          </Grid>
        </Grid>
        <Grid container className={classNames(classes.routeDetailAir,classes.paddingTopUnit)}>
          <Grid sm={4}>
            <Logos size="medium">
              <AirLogo code={airDetail.airlineCode} name={airDetail.airlineDescription} key={airDetail.airlineCode} />
            </Logos>
          </Grid>
          <Grid className={classes.routeDetailAirInfo} sm={8}>
            <Grid className={classes.routeDetailAirInfo} >
              <Typography noWrap variant="body1" style={{width:132,wordWrap:'break-word'}}>
                {airDetail.airlineDescription}
              </Typography>
            </Grid>
            <Grid className={classNames(classes.routeDetailAirInfo,classes.paddingTopHalfUnit)}>
              <Typography variant={'caption'}>
                {airDetail.flightNo}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid className={classNames(classes.fareBrandNameWarp, classes.paddingTopUnit)}>
          <Typography variant={'body1'}>
            {airDetail.fareInfo.fareBrandName}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

const styles = (theme: Theme) => {
  const {
    between: {
      card: { header: cardHeaderSpacing },
    },
  } = spacings(theme);

  return createStyles({
    routeDetailWarp: {
      width: '233px',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      display: 'inline-flex',
      borderLeft: '1px #E0E0E0 solid',
      borderTop: '1px #E0E0E0 solid',
      boxSizing: 'border-box',
      height: '233px',
      backgroundColor: 'white',
    },
    borderRight: {
      borderRight: '1px #E0E0E0 solid',
    },
    backGroundColorSelected: {
      backgroundColor: '#F5F5F5',
    },
    routeDetail: {
      flex: '1 1 0',
      padding: theme.spacing.unit * 3,
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      display: 'inline-flex',
    },
    routeDetailTime: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      display: 'flex',
    },
    routeDetailTimeExtend: {
      alignSelf: 'stretch',
    },
    routeDetailTimeText: {
      color: 'rgba(0, 0, 0, 0.87)',
      fontSize: 16,
      fontFamily: 'Roboto Medium',
      fontWeight: 500,
      letterSpacing: 0.15,
      wordWrap: 'break-word',
      fontStyle: 'normal',
    },
    routeDetailCity: {
      color: 'rgba(0, 0, 0, 0.54)',
      fontSize: 12,
      fontFamily: 'Roboto',
      fontWeight: 400,
      letterSpacing: 0.4,
      wordWrap: 'break-word',
    },
    routeDetailAir: {
      width: '100%',
      height: '100%',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      gap: 8,
      display: 'inline-flex',
    },
    routeDetailAirIcon: {},
    routeDetailAirInfo: {
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      gap: 4,
      display: 'inline-flex',
    },
    routeDetailAirText: {
      color: 'rgba(0, 0, 0, 0.87)',
      fontSize: 14,
      fontFamily: 'Roboto',
      fontWeight: 400,
      letterSpacing: 0.15,
      wordWrap: 'break-word',
      width: '132px',
    },
    fareBrandNameWarp: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      display: 'flex',
    },
    fareBrandNameText: {
      color: 'rgba(0, 0, 0, 0.87)',
      fontFamily: 'Roboto',
      fontWeight: 400,
      letterSpacing: 0.15,
      wordWrap: 'break-word',
    },
    extendWarp: {
      ...cardHeaderSpacing,
    },
    paddingTopUnit: {
        paddingTop: theme.spacing.unit,
    },
    paddingTopHalfUnit: {
      paddingTop: theme.spacing.unit / 2,
    },
    timeLineHeight: {
      lineHeight:2
    },

  });
};

const mapStateToProps = (state: AppState): StateProps => {
  return {
    status: getStatus(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  runInit: () => {
    dispatch(combinationInit());
  },
});

const enhance = compose<CombinationRouteDetailProps, OwnProps>(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
  injectIntl,
);

export const CombinationRouteDetail = enhance(_CombinationRouteDetailBase);

CombinationRouteDetail.displayName = 'CombinationRouteDetail';
